import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { escapeKeyInProgress, isTopDismissLayer, outsideOf, useDismissLayer } from "../lib/dismissLayers";
import { useUnsavedInput } from "../lib/unsavedInput";
import { DialogLiveRegion } from "./DialogLiveRegion";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface DialogProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Sticky action row; on a phone the dialog is a full-screen sheet and this row stays at the bottom. */
  footer?: ReactNode;
  /** false: no Close button, and Esc and an outside click do nothing (e.g. session expired). */
  dismissible?: boolean;
  /** While a request is in flight the dialog cannot be dismissed, so a submission is never orphaned. */
  busy?: boolean;
  wide?: boolean;
  /** GRF-44 (batch 17): "reading" is a document-sized box (about 960 px wide, nearly full height, the body scrolling under a
   * sticky head and foot) for reading a file; below 760 px it is the full-screen sheet like every dialog. */
  size?: "reading";
  /** Below 760 px, a bottom sheet (confirm at the thumb) instead of a full-screen sheet (UX-01 4.11, Revision 3.5). */
  sheet?: boolean;
  /** A right-anchored, full-height panel (the DrawerLayout overlay's own look) instead of a centered box — for a
   * quick-look popup that should read like the Service Desk ticket drawer, e.g. CardPreviewDialog. */
  side?: boolean;
  describedBy?: string;
  /** Unsaved work the form controls cannot show (e.g. a restored draft): an outside click or Esc asks first. */
  unsaved?: boolean;
}

/** The point of a pointer event lies outside the element's box (a click on a modal <dialog>'s ::backdrop targets
 * the <dialog> itself, as does a click on its own scrollbar, so the target alone cannot tell them apart). */
function outsideBox(event: MouseEvent, element: Element): boolean {
  const box = element.getBoundingClientRect();
  return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
}

/**
 * Native modal dialog (focus trap and inert background from the browser). On open, focus goes to [data-autofocus],
 * else the first control in the body, else the first footer action, never the Close button; on close it returns to
 * the opener.
 *
 * Dismissing (ruling 2026-10-01): Esc or a click on the dimmed area outside closes it, unless busy or not
 * dismissible. If it holds unsaved input (a changed field, a typed comment, the `unsaved` prop) it asks "Discard your
 * changes?" first; Keep editing leaves everything as typed. A drag that starts inside never closes it, and an open
 * popover, menu or combobox list inside closes first (shared/lib/dismissLayers). Below 760 px it is a full-screen
 * sheet (screens.css).
 */
export function Dialog({ open, title, onClose, children, footer, dismissible = true, busy = false, wide = false, size, sheet = false, side = false, describedBy, unsaved = false }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const tracker = useUnsavedInput(ref, open);
  const shown = useRef(false);

  function requestDismiss() {
    if (!dismissible || busy || confirming) return;
    if (unsaved || tracker.isDirty()) setConfirming(true);
    else onClose();
  }

  const layer = useDismissLayer(open, {
    element: () => ref.current,
    isOutside: (event) => {
      const dialog = ref.current;
      if (!dialog) return false;
      return event.target === dialog ? outsideBox(event, dialog) : outsideOf(event, dialog);
    },
    dismiss: requestDismiss,
  });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
      shown.current = true;
      tracker.reset();
      const body = dialog.querySelector<HTMLElement>(".gf-dialog-body");
      const foot = dialog.querySelector<HTMLElement>(".gf-dialog-foot");
      const target =
        dialog.querySelector<HTMLElement>("[data-autofocus]") ?? body?.querySelector<HTMLElement>(FOCUSABLE) ?? foot?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    }
    if (!open && dialog.open) {
      dialog.close();
      shown.current = false;
      const previous = opener.current;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    }
  }, [open, tracker]);

  // Most screens unmount an open dialog rather than setting open=false; focus still goes back to the opener.
  useEffect(
    () => () => {
      if (!shown.current) return;
      shown.current = false;
      const previous = opener.current;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    },
    [],
  );

  // Closed from outside while asking: the question goes with it.
  const asking = open && confirming;

  return (
    <dialog
      ref={ref}
      className={["gf-dialog", wide ? "wide" : "", size ?? "", sheet ? "sheet" : "", side ? "side" : ""].filter(Boolean).join(" ")}
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      aria-busy={busy || undefined}
      onCancel={(event) => {
        event.preventDefault();
        // An Esc key was already routed to the innermost layer; a cancel with no key behind it (the Android back
        // gesture) is a dismiss request for this dialog if it is the innermost one.
        if (event.target !== event.currentTarget || escapeKeyInProgress() || !isTopDismissLayer(layer)) return;
        requestDismiss();
      }}
    >
      {open && (
        <>
          <div className="gf-dialog-head">
            <h2 id={titleId}>{title}</h2>
            {dismissible && (
              <button type="button" className="btn sec gf-dialog-x" onClick={onClose} disabled={busy}>
                Close
              </button>
            )}
          </div>
          <div className="gf-dialog-body">{children}</div>
          {footer && <div className="gf-dialog-foot">{footer}</div>}
          {/* Batch 26: where the app's announcements speak while this is the top-most dialog (the page's region is hidden). */}
          <DialogLiveRegion />
          <DiscardChangesPrompt
            open={asking}
            onKeep={() => setConfirming(false)}
            onDiscard={() => {
              setConfirming(false);
              onClose();
            }}
          />
        </>
      )}
    </dialog>
  );
}

/** "Discard your changes?" (Discard / Keep editing). Dismissing the question itself keeps editing. */
export function DiscardChangesPrompt({ open, onKeep, onDiscard }: { open: boolean; onKeep: () => void; onDiscard: () => void }) {
  const bodyId = useId();
  return (
    <Dialog
      open={open}
      sheet
      title="Discard your changes?"
      onClose={onKeep}
      describedBy={bodyId}
      footer={
        <>
          <button type="button" className="btn sec" data-autofocus onClick={onKeep}>
            Keep editing
          </button>
          <button type="button" className="btn danger" onClick={onDiscard}>
            Discard
          </button>
        </>
      }
    >
      <p id={bodyId}>What you typed here has not been saved. Closing now loses it.</p>
    </Dialog>
  );
}
